import { SETTINGS_PREFIX } from '#pplx/frontend/apps/ask/settings/constants.js';
const SETTINGS_HASH = '#settings';
const DEFAULT_SUB_PATH = '/details';
/**
 * First-segment aliases applied when translating between the `#settings`
 * hash and the canonical `/account/<rest>` path. The hash is the
 * user-facing URL, so prefer friendly names there; the account path is
 * what the existing route map is keyed on.
 *
 * `#settings/account` ↔ `/account/details` — the Account page lived at
 * `/details` historically, but `account` reads better in the URL.
 */
const HASH_TO_ACCOUNT_ALIAS = {
    account: 'details',
};
const ACCOUNT_TO_HASH_ALIAS = Object.fromEntries(Object.entries(HASH_TO_ACCOUNT_ALIAS).map(([hash, path]) => [path, hash]));
/**
 * Whether a path is the account settings root or one of its sub-paths. Matches
 * `/account` exactly or `/account/...`, but not siblings that merely share the
 * prefix (e.g. `/accounting`), so those pass through the conversions untouched.
 */
const isAccountPath = (path) => path === SETTINGS_PREFIX || path.startsWith(`${SETTINGS_PREFIX}/`);
const isOrgAccountPath = (path) => path === `${SETTINGS_PREFIX}/org` || path.startsWith(`${SETTINGS_PREFIX}/org/`);
const replaceFirstSegment = (path, aliases) => {
    const trimmed = path.startsWith('/') ? path.slice(1) : path;
    if (!trimmed)
        return path;
    const segments = trimmed.split('/');
    const head = segments[0] ?? '';
    const aliased = aliases[head] ?? head;
    return `/${[aliased, ...segments.slice(1)].join('/')}`;
};
/**
 * Strip the `#settings` prefix and split the remainder into the sub-path
 * (e.g. `/preferences`) and the hash-borne query string. Returns `null` if the
 * hash is not a settings hash. An empty or missing sub-path defaults to
 * `/details` (the Account page).
 */
const parseSettingsHash = (hash) => {
    if (!hash || !hash.startsWith(SETTINGS_HASH))
        return null;
    const rest = hash.slice(SETTINGS_HASH.length);
    const queryIndex = rest.indexOf('?');
    const path = queryIndex === -1 ? rest : rest.slice(0, queryIndex);
    const search = queryIndex === -1 ? '' : rest.slice(queryIndex);
    // Reject non-settings hashes that merely share the prefix (e.g. `#settingsx`).
    if (path !== '' && path !== '/' && !path.startsWith('/'))
        return null;
    const subPath = path === '' || path === '/' ? DEFAULT_SUB_PATH : path;
    return { subPath, search };
};
/**
 * The params carried inside a settings hash (after its own `?`), or `null`
 * when the hash is not a settings hash. Settings search reads its target
 * setting from here while the settings modal is open, since the modal
 * navigates by hash rather than by the page URL.
 */
export const getSettingsHashSearchParams = (hash) => {
    const parsed = parseSettingsHash(hash);
    if (parsed === null)
        return null;
    return new URLSearchParams(parsed.search);
};
/**
 * Map a settings sub-path (e.g. `/preferences`) to its canonical hash
 * (e.g. `#settings/preferences`). The default sub-path collapses to `#settings`.
 */
const buildSettingsHash = (subPath = DEFAULT_SUB_PATH) => {
    const normalized = subPath.startsWith('/') ? subPath : `/${subPath}`;
    if (normalized === DEFAULT_SUB_PATH)
        return SETTINGS_HASH;
    return `${SETTINGS_HASH}${normalized}`;
};
/**
 * Convert a `/account/<rest>` path to the sub-path used after `#settings`,
 * applying the canonical alias for the Account page (`details` → `account`).
 * Anything outside `/account` passes through unchanged.
 */
export const accountPathToSettingsSubPath = (accountPath) => {
    if (!isAccountPath(accountPath) || isOrgAccountPath(accountPath))
        return accountPath;
    const rest = accountPath.slice(SETTINGS_PREFIX.length) || DEFAULT_SUB_PATH;
    return replaceFirstSegment(rest, ACCOUNT_TO_HASH_ALIAS);
};
/**
 * Convert a legacy `/account/<rest>` path to a `#settings/<rest>` hash,
 * applying the canonical alias for the Account page (`details` → `account`).
 * Anything outside `/account` passes through unchanged.
 */
export const accountPathToSettingsHash = (accountPath) => {
    if (!isAccountPath(accountPath) || isOrgAccountPath(accountPath))
        return accountPath;
    return buildSettingsHash(accountPathToSettingsSubPath(accountPath));
};
/**
 * Convert a legacy org-settings hash into its canonical full-page URL. Org
 * settings never render in the personal settings modal.
 */
export const settingsHashToOrgAccountUrl = (hash) => {
    const parsed = parseSettingsHash(hash);
    if (parsed === null || (parsed.subPath !== '/org' && !parsed.subPath.startsWith('/org/'))) {
        return null;
    }
    return `${SETTINGS_PREFIX}${parsed.subPath}${parsed.search}`;
};
/**
 * Convert a `#settings/<rest>` hash back to the canonical `/account/<rest>` path
 * that the existing route map is keyed on, reversing the Account alias. Any
 * query string carried inside the hash is dropped — callers that need it read
 * it via `getSettingsHashSearchParams`.
 */
export const settingsHashToAccountPath = (hash) => {
    const parsed = parseSettingsHash(hash);
    if (parsed === null || parsed.subPath === '/org' || parsed.subPath.startsWith('/org/'))
        return null;
    const aliased = replaceFirstSegment(parsed.subPath, HASH_TO_ACCOUNT_ALIAS);
    return `${SETTINGS_PREFIX}${aliased}`;
};
export const isSettingsHash = (hash) => parseSettingsHash(hash) !== null;
/**
 * Add a settings hash to the current URL without changing the path or search.
 * Uses the router so back navigation returns to the previous page, and
 * dispatches a `hashchange` event so `SettingsModalOpener` picks it up.
 *
 * Router does NOT emit updates for a change in hash, which is why we
 * dispatch a `hashchange` event.
 */
export const openSettings = (router, subPath) => {
    if (typeof window === 'undefined')
        return;
    const targetHash = buildSettingsHash(subPath);
    const { pathname, search } = window.location;
    router.push(`${pathname}${search}${targetHash}`, undefined, 'open settings modal');
    window.dispatchEvent(new HashChangeEvent('hashchange'));
};
//# sourceMappingURL=settingsHash.js.map